import * as React from "react";
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { arFont, enFont } from "@/styles/font";
import { useI18n } from "@/i18n/I18nProvider";

export default function FeedbackSlider({ config, rate, onChange }) {
  const { locale } = useI18n();
  const isArabic = locale === "ar";

  const toArabicDigits = (num) => new Intl.NumberFormat("ar-EG").format(num);

  const marks = Array.from({ length: 11 }, (_, i) => ({
    value: i,
    label: isArabic ? toArabicDigits(i) : String(i),
  }));

  const theme = React.useMemo(
    () => createTheme({ direction: isArabic ? "rtl" : "ltr" }),
    [isArabic]
  );

  const getTrackColor = (v) => {
    if (v <= 6) return "#FFDE22";
    if (v <= 8) return "#FDBA33";
    return "#282781";
  };

  const currentColor = getTrackColor(rate);

  const handleChange = (_, v) => {
    if (onChange) onChange(v);
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        className={locale === "en" ? enFont.className : arFont.className}
        sx={{
          width: "90%",
          mx: "auto",
          direction: isArabic ? "rtl" : "ltr",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          sx={{ fontSize: 12, color: "#000814", fontFamily: "inherit" }}
        >
          {config.question}
        </Typography>

        <Box sx={{ position: "relative", width: "100%" }}>
          <Slider
            value={rate}
            marks={marks}
            onChange={handleChange}
            min={0}
            max={10}
            step={1}
            track="normal"
            sx={{
              height: 22,
              cursor: "pointer",
              "&.MuiSlider-root": {
                padding: "10px 0",
              },
              "& .MuiSlider-rail": {
                width: "102%",
                height: "22px",
                opacity: 1,
                backgroundColor: "white",
                borderWidth: "0.5px",
                borderRadius: "38px",
                border: "0.5px solid #CDD5DF",
              },
              "& .MuiSlider-track": {
                height: "22px",
                borderRadius: "38px",
                backgroundColor: currentColor,
                border: "none",
              },
              "& .MuiSlider-thumb": {
                width: 28,
                height: 28,
                backgroundColor: "#fff",
                border: `.5px solid ${currentColor}`,
                borderRadius: "50%",
                position: "relative",
                zIndex: 1,
                opacity: '0.9',
                boxShadow: "none !important",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  width: 16,
                  height: 16,
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  borderRadius: "50%",
                  backgroundColor: currentColor,
                },
                "&:hover, &:focus, &.Mui-active": {
                  boxShadow: "none !important",
                },
                right: isArabic ? `${-9 + rate * 10}% !important` : "auto",
              },

              // mark under slider 
               "& .MuiSlider-mark": {
                width: "5px",
                height: "0.5px",
                borderRadius: "55px",
                transform: "rotate(90deg)",
                top: { xs: 34, md: 34 },
                backgroundColor: "#aaa",
              },

              //Move the first dash (0) inside the slider a little, depending on the situation
              "& .MuiSlider-mark[data-index='0']": {
                transform: isArabic
                  ? "translateX(-6px) rotate(90deg)"
                  : "translateX(6px) rotate(90deg)",
              },
              // Move the last dash (10) inside the slider a little to the other side
              "& .MuiSlider-mark[data-index='10']": {
                transform: isArabic
                  ? "translateX(2px) rotate(90deg)"
                  : "translateX(-3px) rotate(90deg)",
              },

              "& .MuiSlider-markActive": {
                backgroundColor: currentColor,
              },

              "& .MuiSlider-markLabel": {
                top: { xs: 35, md: 36 },
                transform: "translateX(-50%)",
                fontSize: 12,
                fontFamily: "inherit",
                color: "#545A62",
              },
            }}
          />

          {/* labels inside slider */}
          <Box
            sx={{
              position: "absolute",
              top: "-8px",
              left: 0,
              right: 0,
              height: "100%",
              px:"6px",
              paddingBottom: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pointerEvents: "none",
            }}
          >
            {[config.labels.low, config.labels.mid, config.labels.high].map(
              (label, index) => {
                const active =
                  (index === 0 && rate <= 3) ||
                  (index === 1 && rate > 3 && rate <= 7) ||
                  (index === 2 && rate > 7);

                const color =
                  index === 2 && rate > 7
                    ? "#FFFFFF"
                    : active
                    ? "#000814"
                    : "#545A62";

                return (
                  <Typography
                    key={index}
                    sx={{
                      fontFamily: "inherit",
                      fontSize: isArabic ? 13 : { xs: 10, sm: 13, md: 13},
                      fontWeight: active ? 600 : 200,
                      color,
                    }}
                  >
                    {label}
                  </Typography>
                );
              }
            )}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
